import {controller, target, attr} from '@github/catalyst'

// Drives the chat replay offset by eye. Chat is stamped when a message reaches
// the server, which is after its author saw the moment they were reacting to —
// broadcast latency puts every message that far late in the VOD. This is where
// that gap gets measured: nudge until a reaction lands on what it reacts to,
// then save the number the pane is previewing.
@controller
class ChatReplayCalibratorElement extends HTMLElement {
  @target declare pane: HTMLElement & {setOffsetAdjust: (seconds: number) => void}
  @target declare offsetInput: HTMLInputElement
  @target declare offsetField: HTMLInputElement
  @target declare readout: HTMLElement
  @target declare direction: HTMLElement

  // The offset already saved on the stream. The pane is serving windows
  // against it, so the preview shift is the difference from here.
  @attr savedOffset = 0

  connectedCallback() {
    requestAnimationFrame(() => this.render())
  }

  // Steps come from buttons carrying data-nudge, in seconds.
  nudge(event: Event) {
    const step = Number((event.currentTarget as HTMLElement).getAttribute('data-nudge'))
    if (!Number.isFinite(step)) return

    this.setOffset(this.offset() + step)
  }

  reset() {
    this.setOffset(0)
  }

  offsetChanged() {
    this.setOffset(this.offset())
  }

  private offset(): number {
    const value = Number(this.offsetInput.value)
    return Number.isFinite(value) ? value : 0
  }

  private setOffset(seconds: number) {
    // Sub-second precision is below what anyone can judge by eye, and it makes
    // the readout noisy.
    this.offsetInput.value = (Math.round(seconds * 10) / 10).toString()
    this.render()
  }

  private render() {
    const offset = this.offset()
    const saved = Number(this.getAttribute('data-saved-offset')) || 0

    this.pane?.setOffsetAdjust(offset - saved)
    if (this.offsetField) this.offsetField.value = offset.toString()

    if (this.readout) {
      this.readout.textContent = `${offset > 0 ? '+' : ''}${offset}s`
    }

    if (this.direction) {
      this.direction.textContent = offset === 0
        ? 'Chat plays exactly where it was sent.'
        : offset < 0
          ? `Chat plays ${Math.abs(offset)}s earlier than it was sent — the usual fix for broadcast latency.`
          : `Chat plays ${offset}s later than it was sent.`
    }
  }
}
